import React, { useState, useRef } from "react";
import {
  Box, CircularProgress, Dialog, DialogContent, DialogTitle,
  Divider, Grid, IconButton, MenuItem, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography, useTheme,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import useFetchObjects from "../../../../api/useFetchObjects";
import useDelete from "../../../../api/useDelete";
import { useAuth } from "../../../../context";
import AmbulanceExpenseForm from "./AmbulanceExpenseForm";

const AmbulanceExpenseList = () => {
  const theme = useTheme();
  const auth = useAuth();
  const token = auth?.user?.token;
  const tableRef = useRef(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [viewItem, setViewItem] = useState(null);

  const { data, isLoading, isError, refetch } = useFetchObjects(
    ["ambulance-expenses"],
    "ambulance-expenses/",
    token
  );
  const { handleDelete, ConfirmDialog } = useDelete("ambulance-expenses", token);

  const rows = Array.isArray(data) ? data : (data?.results || []);
  const total = rows.reduce((s, r) => s + parseFloat(r.amount || 0), 0);

  const handleClose = () => { setFormOpen(false); setEditItem(null); refetch(); };

return (
    <Grid container mb={4} mt={2} justifyContent="center" sx={{ display: "none" }}>
      <Grid item xs={12} md={12}>
        {/* Table */}
        {isLoading ? (
          <Box textAlign="center" p={4}><CircularProgress /></Box>
        ) : isError ? (
          <Box textAlign="center" color="error.main" p={4}><Typography>Failed to load data.</Typography></Box>
        ) : (
          <TableContainer ref={tableRef} component={Paper} sx={{ borderRadius: 2, boxShadow: theme.shadows[3] }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: theme.palette.grey[100] }}>
                <TableRow>
                  {["#","Date","Ambulance Code","Plate No.","Driver","Category","Source","Amount","Actions"].map((h) => (
                    <TableCell key={h} align="center" sx={{ fontWeight: 700 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4 }}>No expenses found.</TableCell></TableRow>
                ) : rows.map((r, i) => (
                  <TableRow key={r.id} hover sx={{ bgcolor: i % 2 === 0 ? "action.hover" : "background.default" }}>
                    <TableCell align="center">{i + 1}</TableCell>
                    <TableCell align="center">{r.expense_date}</TableCell>
                    <TableCell align="center">{r.ambulance_code}</TableCell>
                    <TableCell align="center">{r.plate_number}</TableCell>
                    <TableCell align="center">{r.driver_display || "—"}</TableCell>
                    <TableCell align="center">{r.expense_type_name || "—"}</TableCell>
                    <TableCell align="center">{r.expense_source_name || "—"}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                      {parseFloat(r.amount).toLocaleString()}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" color="info" onClick={() => setViewItem(r)}><VisibilityIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="primary" onClick={() => { setEditItem(r); setFormOpen(true); }}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(r.id)}><DeleteIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: theme.palette.grey[100] }}>
                  <TableCell colSpan={7} sx={{ fontWeight: 700 }}>Total</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                    {total.toLocaleString()}
                  </TableCell>
                  <TableCell colSpan={1} />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Grid>

      {/* Add/Edit Dialog */}
      <Dialog open={formOpen} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {editItem ? "Edit Ambulance Expense" : "Add Ambulance Expense"}
          <IconButton onClick={handleClose}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <AmbulanceExpenseForm expense={editItem} onSuccess={handleClose} token={token} />
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={!!viewItem} onClose={() => setViewItem(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Expense Details
          <IconButton onClick={() => setViewItem(null)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {viewItem && [
            ["Date", viewItem.expense_date],
            ["Ambulance Code", viewItem.ambulance_code],
            ["Plate Number", viewItem.plate_number],
            ["Driver", viewItem.driver_name || "—"],
            ["Category", viewItem.expense_type_name || "—"],
            ["Expense Source", viewItem.expense_source_name || "—"],
            ["Amount", parseFloat(viewItem.amount).toLocaleString()],
            ["Invoice No.", viewItem.invoice_number || "—"],
            ["Description", viewItem.description || "—"],
          ].map(([label, val]) => (
            <Grid container key={label} sx={{ py: 0.75, borderBottom: "1px solid #f0f0f0" }}>
              <Grid item xs={5}><Typography fontWeight={600} variant="body2">{label}</Typography></Grid>
              <Grid item xs={7}><Typography variant="body2">{val}</Typography></Grid>
            </Grid>
          ))}
          {viewItem?.attachment && (
            <Box mt={1}>
              <Button variant="outlined" size="small" component="a"
                href={viewItem.attachment} target="_blank" download>
                Download Attachment
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog />
    </Grid>
  );
};

export default AmbulanceExpenseList;
